import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Eye, Moon, Star, Zap, Lightbulb } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { trackEvent } from '@/services/eventTracker';
import { ShareResultCard } from './ShareResultCard';

// Level icons with neon colors
const LevelIcon = ({ level }) => {
  const icons = {
    1: { Icon: Eye, color: '#94a3b8', glow: 'rgba(148, 163, 184, 0.5)' },
    2: { Icon: Sparkles, color: '#22d3ee', glow: 'rgba(34, 211, 238, 0.5)' },
    3: { Icon: Moon, color: '#a78bfa', glow: 'rgba(167, 139, 250, 0.5)' },
    4: { Icon: Star, color: '#f472b6', glow: 'rgba(244, 114, 182, 0.5)' },
    5: { Icon: Zap, color: '#fbbf24', glow: 'rgba(251, 191, 36, 0.6)' }
  };
  
  const { Icon, color, glow } = icons[level] || icons[1];
  
  return (
    <div 
      className="w-16 h-16 rounded-full flex items-center justify-center"
      style={{ 
        backgroundColor: `${color}20`,
        boxShadow: `0 0 30px ${glow}, 0 0 60px ${glow}`
      }}
    >
      <Icon className="w-8 h-8" style={{ color }} />
    </div>
  );
};

const getPsychicLevel = (hits, total) => {
  const percentage = (hits / total) * 100;
  
  // Seviye 1: %20 ve altı (rastgele şans)
  if (percentage <= 20) return {
    level: 1,
    name: 'Normal Yetenek',
    description: 'Bu test sonucu, rastgele tahmin seviyesinde bir performans gösteriyor. Sezgisel algı henüz belirgin bir sinyal vermemiş olabilir veya zihinsel gürültü algıyı maskeliyor olabilir.',
    note: 'Endişelenmeyin - bu sadece bir başlangıç noktası. Düzenli pratik ve odaklanma egzersizleri ile sezgisel algınızı güçlendirebilirsiniz.',
    color: '#94a3b8'
  };
  
  // Seviye 2: %21-28
  if (percentage <= 28) return {
    level: 2,
    name: 'Kaydedilir Yetenek',
    description: 'Sonuçlarınız rastgele şansın üzerinde bir performans gösteriyor. Sezgisel sinyaller mevcut ancak henüz tutarlı değil. Bu seviye, gelişmekte olan psişik potansiyelin ilk işaretlerini içerir.',
    note: 'İyi bir başlangıç! Sezgisel algınız uyanıyor. Meditasyon ve üçüncü göz aktivasyon egzersizleri ile bu yeteneği güçlendirebilirsiniz.',
    color: '#22d3ee'
  };
  
  // Seviye 3: %29-40
  if (percentage <= 40) return {
    level: 3,
    name: 'Psişik Şaman',
    description: 'Dikkat çekici bir performans! Sonuçlarınız, aktif sezgisel algının varlığını gösteriyor. Şamanik geleneklerde bu seviye, ruhsal rehberlikle bağlantı kurabilme yeteneğinin işareti olarak kabul edilir.',
    note: 'Etkileyici! Sezgisel kanallarınız açılıyor. Düzenli pratik ile bu yeteneği daha da derinleştirebilir ve diğer psişik yeteneklerinizi keşfedebilirsiniz.',
    color: '#a78bfa'
  };
  
  // Seviye 4: %41-55
  if (percentage <= 55) return {
    level: 4,
    name: 'İleri Düzey Durugörü',
    description: 'Olağanüstü bir performans sergiliyorsunuz! Bu seviye, güçlü ve tutarlı sezgisel algıyı gösterir. Durugörü yeteneğiniz aktif ve gelişmiş durumda.',
    note: 'Muhteşem! Psişik yetenekleriniz ileri düzeyde. Bu seviyeyi korumak ve geliştirmek için düzenli meditasyon ve enerji çalışmaları önerilir.',
    color: '#f472b6'
  };
  
  // Seviye 5: %56+
  return {
    level: 5,
    name: 'Olağanüstü Yetenek',
    description: 'İnanılmaz! Bu seviye son derece nadir görülür. Sezgisel algınız olağanüstü güçte ve netlikte. Psişik yetenekleriniz en üst düzeyde aktif.',
    note: 'Efsanevi bir performans! Bu seviyedeki sonuçlar çok nadir görülür. Yeteneklerinizi korumak ve başkalarına rehberlik etmek için ileri düzey pratikleri keşfedin.',
    color: '#fbbf24'
  };
};

// Durugörü Tips Component
const DurugoruTips = () => {
  const tips = [
    'Teste başlamadan önce 3 derin nefes al ve zihninizi boşaltın',
    'Gözlerinizi hafifçe kapatıp kaşlarınızın arasına odaklanın',
    'İlk içgüdüsel hisse güvenin, düşünmeyin',
    'Her seçimden önce kalbinizin sesini dinleyin'
  ];
  
  return (
    <div className="glass-panel p-4 space-y-3 border-amber-500/20">
      <div className="flex items-center gap-2 text-amber-400">
        <Lightbulb className="w-4 h-4" />
        <span className="text-xs tracking-widest uppercase font-medium">Durugörü İpuçları</span>
      </div>
      <ul className="space-y-2">
        {tips.map((tip, index) => (
          <li key={index} className="text-xs text-slate-400 flex items-start gap-2">
            <span className="text-amber-400/60">✦</span>
            {tip}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const SummaryScreen = ({ session, onNewSession, savedSession, onOpenTraining, onNextStage }) => {
  const accuracy = session?.total > 0 ? ((session.hits / session.total) * 100).toFixed(1) : '0.0';
  
  // Calculate expected accuracy based on random chance (20% for 5 symbols)
  const expectedAccuracy = 20;
  const difference = (parseFloat(accuracy) - expectedAccuracy).toFixed(1);
  const differenceText = difference >= 0 ? `+${difference}%` : `${difference}%`;
  
  // Get psychic level based on hits
  const psychicLevel = getPsychicLevel(session?.hits || 0, session?.total || 25);
  
  // Prepare timeline data
  const timelineData = session?.guessHistory?.map((guess, index) => ({
    round: index + 1,
    result: guess.isHit ? 1 : 0,
    label: guess.isHit ? 'İsabet' : 'Kaçan'
  })) || [];
  
  // Prepare symbol performance data
  const symbolStats = savedSession?.symbolStats || {};
  const symbolData = Object.entries(symbolStats).map(([symbol, stats]) => ({
    symbol: symbol.charAt(0).toUpperCase() + symbol.slice(1),
    Tahmin: stats.guessed,
    Gerçek: stats.actual,
    İsabet: stats.hits
  }));
  
  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="summary-screen">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <motion.div
          className="text-center space-y-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl md:text-5xl font-mystic text-slate-50 text-glow">
            Test Tamamlandı
          </h1>
          <p className="text-slate-400">
            Testiniz tamamlandı, işte sonuçlarınız.
          </p>
        </motion.div>
        
        {/* Psychic Level - Prominent Display */}
        <motion.div
          className="glass-panel p-8 space-y-6 border-2"
          style={{ borderColor: `${psychicLevel.color}30` }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex flex-col md:flex-row items-center gap-6">
            <LevelIcon level={psychicLevel.level} />
            <div className="text-center md:text-left flex-1">
              <p className="text-xs tracking-widest uppercase text-slate-500 mb-1">
                Psişik Seviye {psychicLevel.level}
              </p>
              <h2 
                className="text-3xl md:text-4xl font-mystic"
                style={{ color: psychicLevel.color, textShadow: `0 0 20px ${psychicLevel.color}50` }}
              >
                {psychicLevel.name}
              </h2>
            </div>
            <div className="text-center">
              <p className="text-5xl font-heading" style={{ color: psychicLevel.color }}>
                {accuracy}%
              </p>
              <p className={`text-xs ${parseFloat(difference) >= 0 ? 'text-green-400' : 'text-slate-500'}`}>
                {differenceText} temel çizgiden
              </p>
            </div>
          </div>
          
          <div className="pt-4 border-t border-white/10 space-y-3">
            <p className="text-slate-300 leading-relaxed">
              {psychicLevel.description}
            </p>
            <p className="text-sm text-slate-400 leading-relaxed italic flex items-start gap-2">
              <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: psychicLevel.color }} />
              {psychicLevel.note}
            </p>
          </div>
        </motion.div>
        
        {/* Main Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="glass-panel p-6 text-center">
            <p className="text-xs tracking-widest uppercase text-slate-600 mb-2">Toplam Tur</p>
            <p className="text-3xl font-heading text-slate-100">{session?.total || 0}</p>
          </div>
          <div className="glass-panel p-6 text-center">
            <p className="text-xs tracking-widest uppercase text-slate-600 mb-2">İsabet</p>
            <p className="text-3xl font-heading text-green-400">{session?.hits || 0}</p>
          </div>
          <div className="glass-panel p-6 text-center">
            <p className="text-xs tracking-widest uppercase text-slate-600 mb-2">Kaçan</p>
            <p className="text-3xl font-heading text-slate-500">{session?.misses || 0}</p>
          </div>
          <div className="glass-panel p-6 text-center">
            <p className="text-xs tracking-widest uppercase text-slate-600 mb-2">En İyi Seri</p>
            <p className="text-3xl font-heading text-cyan-400">{session?.bestStreak || 0}</p>
          </div>
        </motion.div>
        
        {/* Charts */}
        {timelineData.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Hit/Miss Timeline */}
            <div className="glass-panel p-6 space-y-4">
              <h2 className="text-xs tracking-widest uppercase text-violet-400 font-medium">
                İsabet/Kaçan Zaman Çizelgesi
              </h2>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="round" stroke="#64748b" style={{ fontSize: '10px' }} />
                  <YAxis
                    stroke="#64748b"
                    style={{ fontSize: '10px' }}
                    ticks={[0, 1]}
                    tickFormatter={(value) => value === 1 ? 'İsabet' : 'Kaçan'}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                  />
                  <Line type="stepAfter" dataKey="result" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6', r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            {/* Symbol Performance */}
            {symbolData.length > 0 && (
              <div className="glass-panel p-6 space-y-4">
                <h2 className="text-xs tracking-widest uppercase text-violet-400 font-medium">
                  Sembol Performansı
                </h2>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={symbolData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="symbol" stroke="#64748b" style={{ fontSize: '10px' }} />
                    <YAxis stroke="#64748b" style={{ fontSize: '10px' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                    />
                    <Bar dataKey="İsabet" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
        
        {/* Share Result Card */}
        <ShareResultCard session={session} psychicLevel={psychicLevel} accuracy={accuracy} />
        
        {/* Durugörü Tips */}
        <DurugoruTips />
        
        {/* Training CTA */}
        <div className="glass-panel p-8 space-y-6 border-violet-500/20 bg-gradient-to-br from-violet-500/5 to-transparent">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
              <Moon className="w-6 h-6 text-violet-400" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <h3 className="text-xl font-heading text-slate-100 mb-2">
                  Yeteneklerinizi Geliştirin
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Durugörü Eğitim Programı ile üçüncü gözünüzü açın ve sezgisel algınızı güçlendirin.
                </p>
              </div>
              
              <button
                onClick={() => {
                  trackEvent.upgradeClicked('summary_training');
                  onOpenTraining && onOpenTraining();
                }}
                className="rounded-full bg-violet-500/20 px-8 py-4 text-sm font-medium text-violet-300 transition-all hover:bg-violet-500/30 border border-violet-500/30"
                data-testid="training-cta-btn"
              >
                Eğitim Programını İncele
              </button>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <button
            onClick={onNewSession}
            className="rounded-full bg-white/5 px-12 py-4 text-sm font-medium text-white transition-all hover:bg-white/10 hover:shadow-[0_0_20px_rgba(139,92,246,0.3)] active:scale-95 border border-white/10"
            data-testid="new-session-btn"
          >
            Yeni Test Başlat
          </button>
          
          {onNextStage && (
            <button
              onClick={onNextStage}
              className="rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 px-12 py-4 text-sm font-medium text-cyan-300 transition-all hover:from-cyan-500/30 hover:to-blue-500/30 hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] active:scale-95 border border-cyan-500/30"
              data-testid="next-stage-btn"
            >
              2. Aşama Teste Geç
            </button>
          )}
        </div>
        
        {/* Disclaimer */}
        <div className="text-center pt-6">
          <p className="text-xs text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Bu uygulama teşhis, tahmin veya garanti edilmiş yetenek değerlendirmesi sağlamaz. 
            Tüm sonuçlar, öz yansıma ve eğitim için amaçlanmış istatistiksel gözlemlerdir.
          </p>
        </div>
      </div>
    </div>
  );
};
